import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import PageHeader from "@/components/PageHeader";
import { History, ExternalLink, Search, X } from "lucide-react";
import { toast } from "sonner";

const ACTION_META = {
  // Legacy actions (history-based, appointment lifecycle)
  criado: { label: "Criado", color: "#0369a1", bg: "#dbeafe" },
  remarcado: { label: "Remarcado", color: "#a16207", bg: "#fef9c3" },
  concluido: { label: "Concluído", color: "#15803d", bg: "#dcfce7" },
  cancelado: { label: "Cancelado", color: "#dc2626", bg: "#fee2e2" },
  status_alterado: { label: "Status alterado", color: "#6b21a8", bg: "#f3e8ff" },
  valor_alterado: { label: "Valor alterado", color: "#9a3412", bg: "#ffedd5" },
  // Fase 1+ (audit_log entries)
  "sale.create": { label: "Venda criada", color: "#15803d", bg: "#dcfce7" },
  "sale.void": { label: "Venda ESTORNADA", color: "#b91c1c", bg: "#fee2e2" },
  "appointment.complete": { label: "Comanda finalizada", color: "#15803d", bg: "#dcfce7" },
  "appointment.void_legacy": { label: "Estorno LEGADO", color: "#b91c1c", bg: "#fee2e2" },
  "commission.reverted": { label: "Comissão revertida", color: "#a16207", bg: "#fef9c3" },
  "credit.deposit": { label: "Depósito de crédito", color: "#0284c7", bg: "#e0f2fe" },
  "invoice.create": { label: "Fiado criado", color: "#7c3aed", bg: "#ede9fe" },
  "transaction.create": { label: "Lançamento manual", color: "#525252", bg: "#f5f5f5" },
  "employee_payment.create": { label: "Pagamento funcionário", color: "#0891b2", bg: "#cffafe" },
};

const formatCurrency = (v) =>
  v == null
    ? "—"
    : new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

const formatDateTime = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return s;
  }
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function Audit() {
  const { apiCall, user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [actors, setActors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [action, setAction] = useState("all");
  const [actor, setActor] = useState("all");
  const [employeeId, setEmployeeId] = useState("all");
  const [startDate, setStartDate] = useState(daysAgoISO(30));
  const [endDate, setEndDate] = useState(todayISO());
  const [search, setSearch] = useState("");

  const isMaster = !user || user.role === "master";

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (action !== "all") params.set("action", action);
      if (actor !== "all") params.set("changed_by_user_id", actor);
      if (employeeId !== "all") params.set("employee_id", employeeId);
      if (startDate) params.set("start_date", new Date(startDate).toISOString());
      if (endDate) {
        const d = new Date(endDate);
        d.setHours(23, 59, 59, 999);
        params.set("end_date", d.toISOString());
      }
      params.set("limit", "500");

      const [ev, ac, emp] = await Promise.all([
        apiCall("get", `/audit/appointments?${params}`),
        apiCall("get", `/audit/actors`),
        apiCall("get", `/employees`),
      ]);
      setEvents(ev.data || []);
      setActors(ac.data || []);
      setEmployees(emp.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao carregar auditoria");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isMaster) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, actor, employeeId, startDate, endDate, isMaster]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) =>
      [
        e.client_name,
        e.employee_name,
        e.changed_by_name,
        e.notes,
        ...(e.services || []),
      ]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q))
    );
  }, [events, search]);

  const stats = useMemo(() => {
    const out = {};
    for (const e of filtered) {
      out[e.action] = (out[e.action] || 0) + 1;
    }
    return out;
  }, [filtered]);

  // Guard: master only (after hooks)
  if (!isMaster) {
    return (
      <div className="space-y-8" data-testid="audit-forbidden">
        <PageHeader overline="Admin" title="Auditoria" />
        <Card className="p-8 text-center text-muted-foreground">
          Acesso restrito ao admin master.
        </Card>
      </div>
    );
  }

  const goToAppointment = (apptId) => {
    if (apptId) navigate(`/agenda?focus=${apptId}`);
  };

  return (
    <div className="space-y-6" data-testid="audit-page">
      <PageHeader
        overline="Admin · Master only"
        title="Auditoria"
        subtitle="Histórico completo de alterações em agendamentos"
      />

      {/* Filters */}
      <Card className="p-4 border border-border bg-card shadow-soft">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              De
            </label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="audit-start-date"
              className="h-9"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Até
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="audit-end-date"
              className="h-9"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Ação
            </label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="audit-action-filter" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(ACTION_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Quem alterou
            </label>
            <Select value={actor} onValueChange={setActor}>
              <SelectTrigger data-testid="audit-actor-filter" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {actors.map((a) => (
                  <SelectItem key={a.user_id} value={a.user_id}>
                    {a.name} ({a.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Profissional
            </label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger data-testid="audit-employee-filter" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.employee_id} value={e.employee_id}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Busca livre
            </label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cliente, observação..."
                className="h-9 pl-8 pr-8"
                data-testid="audit-search"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Quick stats */}
        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-border" data-testid="audit-stats">
          <Badge variant="outline" className="text-xs">
            {filtered.length} eventos
          </Badge>
          {Object.entries(stats).map(([k, n]) => {
            const meta = ACTION_META[k] || { label: k, color: "#374151", bg: "#f3f4f6" };
            return (
              <Badge
                key={k}
                variant="outline"
                className="text-xs"
                style={{ color: meta.color, backgroundColor: meta.bg, borderColor: meta.bg }}
              >
                {meta.label}: {n}
              </Badge>
            );
          })}
        </div>
      </Card>

      {/* Table */}
      <Card className="border border-border bg-card shadow-soft rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/30 hover:bg-secondary/30">
              <TableHead className="text-xs uppercase tracking-widest font-medium">Quando</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Ação</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Quem</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Cliente · Profissional</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Detalhes</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest font-medium">Valor</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest font-medium">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  <History className="h-8 w-8 mx-auto mb-2 opacity-40" strokeWidth={1.2} />
                  Nenhum evento encontrado com esses filtros.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((ev, idx) => {
                const meta = ACTION_META[ev.action] || {
                  label: ev.action,
                  color: "#374151",
                  bg: "#f3f4f6",
                };
                return (
                  <TableRow key={`${ev.appointment_id}-${idx}`} data-testid={`audit-row-${idx}`}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {formatDateTime(ev.changed_at)}
                    </TableCell>
                    <TableCell>
                      <span
                        className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md font-medium"
                        style={{ color: meta.color, backgroundColor: meta.bg }}
                      >
                        {meta.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {ev.changed_by_name || (
                        <span className="text-muted-foreground italic">sistema</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium">{ev.client_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">
                        {ev.employee_name || "sem profissional"} ·{" "}
                        {(ev.services || []).join(", ") || "sem serviço"}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[320px]">
                      {ev.notes && (
                        <div className="truncate" title={ev.notes}>
                          {ev.notes}
                        </div>
                      )}
                      {(ev.old_value || ev.new_value) && (
                        <div className="text-[10px] mt-0.5">
                          {ev.old_value && (
                            <span className="line-through text-red-600/70 mr-1">
                              {ev.old_value}
                            </span>
                          )}
                          {ev.new_value && (
                            <span className="text-emerald-700">→ {ev.new_value}</span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm font-medium" style={{ color: "#CBA871" }}>
                      {formatCurrency(ev.total_price)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => goToAppointment(ev.appointment_id)}
                        data-testid={`audit-open-${idx}`}
                        title="Abrir agendamento"
                      >
                        <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
